import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { getBookingTime } from '@us/web/cart/util';
import { OrdersActions } from '@us/web/orders/data-access';
import { AppInit } from '@us/web/shared/app';
import {
    BusinessModel,
    DeliveryFee,
    Order,
    OrderDelivery,
    OrderItem,
    OrderMember,
    OrderState,
    OrderType,
    PaymentType,
    Table
} from '@us/web/shared/data-access';
import {
    calcTotalDiscount,
    calcTotalDuration,
    calcTotalItems,
    calcTotalPrice,
    getErrorMessage,
    getObjectDifferences
} from '@us/web/shared/utils';
import { formatISO, isBefore, parseISO } from 'date-fns';
import { cloneDeep, includes } from 'lodash';
import { CartActions } from './cart.actions';
import { captureMessage } from '@sentry/angular-ivy';

export const CART_FEATURE_KEY = 'cart';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CartMember extends OrderMember {}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CartItem extends OrderItem {}

export interface Cart extends Order {
    items: CartItem[];
    delivery_fees?: DeliveryFee[] | null;
}

export type CartFillable = Partial<
    Pick<
        Cart,
        | 'order_code'
        | 'payment_type'
        | 'discount'
        | 'surcharge'
        | 'tax'
        | 'grand_total'
        | 'paid_total'
        | 'duration_total'
        | 'note'
        | 'shop'
        | 'order_type'
        | 'order_table'
        | 'booking_time'
        | 'receiver'
        | 'delivery'
        | 'is_shared'
        | 'group_members'
        | 'payment_detail'
    >
>;

export interface State {
    cart: Cart;
    /** @deprecated */
    loading: boolean;
    error: string | null;
    loadingTable: boolean;
}

export const getEmptyCart = (params: CartFillable = {}): Cart => ({
    order_id: '',
    order_code: '',
    payment_type: PaymentType.Cash,
    grand_total: 0,
    paid_total: 0,
    note: '',
    items: [],
    duration_total: 0,
    current_state: OrderState.Pending,
    order_type: null, // OrderType.Delivery,
    total_price: 0,
    total_items: 0,
    version_code: 0,
    booking_time: formatISO(new Date()),
    is_shared: false,
    group_members: [],
    delivery_fees: null,
    ...params
});

export const initialState = (params: CartFillable = {}): State => ({
    cart: getEmptyCart(params),
    loading: false,
    error: null,
    loadingTable: false
});

/** @deprecated */
export const refreshCart = (cart: Cart): Cart => {
    const totalPrice = calcTotalPrice(cart.items);
    const totalItems = calcTotalItems(cart.items);
    const durationTotal = calcTotalDuration(cart.items);
    let totalDiscount = calcTotalDiscount(cart.discount, totalPrice);
    if (cart.discount) {
        // reload discount when update cart
        cart.discount = { ...cart.discount, price: totalDiscount };
        const isInvalidMinBillValue = cart.discount.minBillValue && cart.discount.minBillValue > totalPrice;
        if (totalItems === 0 || isInvalidMinBillValue) {
            cart.discount = { ...cart.discount, price: 0, percent: 0 };
            totalDiscount = 0;
        }
    }
    const grandTotal =
        totalPrice -
        totalDiscount -
        (cart.tax?.price || 0) +
        (cart.surcharge?.price || 0) +
        (cart.delivery?.delivery_fee || 0);

    const bookingTime =
        cart.version_code === 0 && isBefore(parseISO(cart.booking_time), new Date())
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              getBookingTime(cart.order_type!)
            : cart.booking_time;

    return {
        ...cart,
        total_price: totalPrice,
        total_items: totalItems,
        duration_total: durationTotal,
        grand_total: grandTotal,
        ...(cart.version_code === 0 ? { booking_time: bookingTime } : {})
    };
};

/** @deprecated */
const changeDeliveryService = (state: State, delivery: OrderDelivery): State => {
    const cart: Cart = cloneDeep(state.cart);
    cart.delivery = delivery;
    return { ...state, cart: refreshCart(cart) };
};

export const cartFeature = createFeature({
    name: CART_FEATURE_KEY,
    reducer: createReducer(
        initialState(),
        on(
            CartActions.createCart,
            CartActions.getCartDetail,
            CartActions.clearCart,
            CartActions.updateCart,
            CartActions.addToCart,
            CartActions.removeFromCart,
            CartActions.updateItemQuantity,
            CartActions.joinSharedOrder,
            CartActions.removeCartMember,
            (state: State): State => ({ ...state, loading: true })
        ),
        on(
            CartActions.createCartSuccess,
            CartActions.getCartDetailSuccess,
            CartActions.clearCartSuccess,
            CartActions.updateCartSuccess,
            CartActions.addToCartSuccess,
            CartActions.removeFromCartSuccess,
            CartActions.updateItemQuantitySuccess,
            CartActions.removeCartMemberSuccess,
            (state: State, { cart }): State => ({
                ...state,
                loading: false,
                cart: { ...state.cart, ...cart }
            })
        ),
        on(CartActions.selectTable, (state: State): State => ({ ...state, loading: true, loadingTable: true })),
        on(CartActions.recoverCartSuccess, (state: State, { cart }): State => ({
            ...state,
            cart,
            error: null
        })),
        on(CartActions.selectTableSuccess, (state: State, { cart }): State => {
            const is_merge_order_qr = localStorage.getItem('is_merge_order_qr');
            return {
                ...state,
                loading: false,
                loadingTable: false,
                cart: is_merge_order_qr ? cart : { ...state.cart, ...cart }
            };
        }),
        on(
            CartActions.joinSharedOrderSuccess,
            (state: State, { cart }): State => ({
                ...state,
                loading: false,
                cart: { ...cart }
            })
        ),
        on(
            CartActions.createCartFailed,
            CartActions.getCartDetailFailed,
            CartActions.clearCartFailed,
            CartActions.updateCartFailed,
            CartActions.addToCartFailed,
            CartActions.removeFromCartFailed,
            CartActions.updateItemQuantityFailed,
            CartActions.joinSharedOrderFailed,
            CartActions.removeCartMemberFailed,
            CartActions.selectTableFailed,
            (state: State, { error }): State => ({ ...state, loading: false, loadingTable: false, error })
        ),
        on(CartActions.changeDeliveryService, (state: State, delivery: OrderDelivery) =>
            changeDeliveryService(state, delivery)
        ),
        on(
            CartActions.submitCart,
            (state: State, { businessModel }): State => ({
                ...state,
                loading: true
            })
        ),
        on(
            CartActions.submitCartFailed,
            (state: State, { error }: any): State => ({
                ...state,
                loading: false,
                error: getErrorMessage(error, 'error')
            })
        ),
        on(CartActions.submitCartSuccess, (state: State, { cart, businessModel }) => {
            if (
                [
                    OrderState.Canceled,
                    OrderState.Rejected,
                    OrderState.Complete,
                    OrderState.PendingPayment,
                    OrderState.PaymentSuccess,
                    OrderState.PaymentFailed
                ].includes(cart.current_state) ||
                ![OrderType.Delivery, OrderType.TakeAway, OrderType.OrderAtTheTable].includes(cart.order_type!) ||
                businessModel === BusinessModel.PrePayment
            ) {
                return initialState();
            }
            return {
                ...state,
                cart,
                loading: false
            };
        }),
        on(CartActions.pushOrderState, (state: State, { new_state }): State => {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    current_state: new_state
                },
                loading: true
            };
        }),
        on(
            CartActions.pushOrderStateSuccess,
            (state: State, { order }): State => ({
                ...state,
                cart: order,
                loading: false
            })
        ),
        on(
            CartActions.pushOrderStateFailed,
            (state: State, { error }): State => ({
                ...state,
                loading: false,
                error
            })
        ),
        on(OrdersActions.loadOrder, OrdersActions.loadOrderPaymentStatus, (state: State, { id }): State => {
            if (id !== state.cart.order_id) {
                return state;
            }

            return {
                ...state,
                loading: true
            };
        }),
        on(OrdersActions.loadOrderSuccess, (state: State, { order }: any): State => {
            if (order.order_id !== state.cart.order_id) {
                return state;
            }

            if (state.cart.version_code > order.version_code) {
                console.warn('Old order version', getObjectDifferences(state.cart, order));
                captureMessage(
                    'Old order version ' + JSON.stringify(getObjectDifferences(state.cart, order)),
                    'warning'
                );
                return state;
            }

            const businessModels =
                (Array.isArray(order.shop?.business_model)
                    ? order.shop?.business_model
                    : [order.shop?.business_model]) || [];

            if (
                order.order_type !== OrderType.OrderAtTheTable ||
                businessModels.indexOf(BusinessModel.PrePayment) !== -1 ||
                includes([OrderState.Canceled, OrderState.Rejected, OrderState.Complete], order.current_state)
            ) {
                return initialState({ order_type: order.order_type });
            }
            return {
                ...state,
                cart: order,
                loading: false
            };
        }),
        on(OrdersActions.loadOrderFailed, (state: State, { error, orderId }: any): State => {
            if (orderId !== state.cart.order_id) {
                return state;
            }
            return {
                ...state,
                loading: false,
                error
            };
        }),
        on(CartActions.resetCartState, (state: State): State => ({ ...state, loading: false })),
        on(AppInit, (state: State): State => ({ ...state, loading: false, loadingTable: false }))
        // on(CartActions.updateCartWithPromotion, (state: State, value: CartFillable) => updateCart(state, value))
        // on(
        //     CartActions.selectShopSuccess,
        //     (state: State, { shop }): State => ({
        //         ...state,
        //         cart: {
        //             ...state.cart,
        //             shop
        //         }
        //     })
        // )
    )
});

export const { name, reducer, selectCartState, selectCart, selectLoading, selectError, selectLoadingTable } = cartFeature;

export const selectOrderId = createSelector(selectCart, (cart: Cart) => cart.order_id);

export const selectCartItems = createSelector(selectCart, (cart: Cart) => cart?.items);

export const selectCartTotalItems = createSelector(selectCart, (cart: Cart) => cart?.total_items || 0);

export const selectCartTotalNewItems = createSelector(selectCartItems, items =>
    items.reduce((total, item) => total + (item.version_code === 0 ? Number(item?.quantity) : 0), 0)
);

export const selectCartTotalNewItemsByUserId = (userId: string) =>
    createSelector(selectIsCartOwner(userId), selectCartItems, (isOwner, items) =>
        items.reduce(
            (total, item) =>
                total + ((isOwner || item.user?.id === userId) && item.version_code === 0 ? Number(item?.quantity) : 0),
            0
        )
    );

export const selectOrderType = createSelector(selectCart, (cart: Cart) => cart?.order_type || null);

export const selectCartTable = createSelector(selectCart, (cart: Cart) => cart?.order_table);

export const selectCartTableId = createSelector(selectCartTable, (table: Table | undefined) => table?.id);

export const selectCartPaymentType = createSelector(selectCart, (cart: Cart) => cart?.payment_type);

export const selectCartTotal = createSelector(selectCart, (cart: Cart) => cart?.grand_total || 0);
export const selectCartTotalPrice = createSelector(selectCart, (cart: Cart) => cart?.total_price || 0);

export const selectItemQuantityInCart = (productId: string) =>
    createSelector(
        selectCartItems,
        items =>
            items?.reduce(
                (t, item) => t + (item.product_id === productId ? item.quantity * (item.unit?.ratio || 1) : 0),
                0
            ) || 0
    );

export const selectShowAddItemButton = createSelector(
    selectCart,
    (cart: Cart) =>
        !cart.paid_total &&
        ![OrderState.Complete, OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(cart.current_state)
);

export const selectIsNewOrder = createSelector(selectCart, (cart: Cart) => cart?.version_code === 0);

export const selectSelectedItemQuantityInCart = (productId: string) =>
    createSelector(
        selectCartItems,
        items =>
            items?.reduce(
                (t, item) =>
                    t +
                    (item.version_code === 0 && item.product_id === productId
                        ? item.quantity * (item.unit?.ratio || 1)
                        : 0),
                0
            ) || 0
    );

export const selectItemInCart = (productId: string) =>
    createSelector(
        selectCartItems,
        items =>
            items?.reduce((t, item) => {
                if (item.product_id !== productId || item.version_code > 0) {
                    return t;
                }
                t.push(item);

                return t;
            }, [] as Array<CartItem>) || []
    );

export const selectCartShop = createSelector(selectCart, (cart: Cart) => cart?.shop);

export const selectCartReceiver = createSelector(selectCart, (cart: Cart) => cart?.receiver || null);

export const selectIsNewCart = createSelector(selectCart, (cart: Cart) => !cart?.version_code);

export const selectCartBookingTime = createSelector(selectCart, (cart: Cart) =>
    cart?.booking_time ? parseISO(cart.booking_time) : null
);

export const selectIsCartShared = createSelector(selectCart, (cart: Cart) => cart?.is_shared || false);

export const selectCartMembers = createSelector(selectCart, (cart: Cart) =>
    cart.is_shared ? cart?.group_members || [] : []
);

export const selectCartMemberCount = createSelector(selectCartMembers, (members: CartMember[]) => members.length);

export const selectIsCartOwner = (userId: string) =>
    createSelector(
        selectCart,
        (cart: Cart) => !cart.is_shared || cart?.group_members?.find(e => e.id === userId)?.is_owner || false
    );

export const selectItemsGroupedByMember = createSelector(selectCart, ({ group_members, items }) => {
    return (group_members || []).map(member => ({
        ...member,
        is_owner: member.is_owner || false,
        items: items.filter(e => e.user?.id === member?.id)
    }));
});

export const selectCartDelivery = createSelector(selectCart, (cart: Cart) => cart?.delivery || null);

export const selectDeliveryFees = (flat = false) =>
    createSelector(selectCart, (cart: Cart) => {
        const deliveryFees = cart?.delivery_fees || [];
        if (!flat) {
            return deliveryFees;
        }

        return deliveryFees
            .flatMap(item =>
                (item.estimate_fees || []).map(
                    estimate_fee =>
                        ({
                            partner_id: item.delivery_partner_id,
                            partner_name: item.delivery_partner_name,
                            service_id: estimate_fee.service_id,
                            service_name: estimate_fee.service_name,
                            delivery_fee: estimate_fee.estimate_fee,
                            delivery_fee_original: estimate_fee.estimate_fee_original
                        } as OrderDelivery)
                )
            )
            .sort((a, b) => a.delivery_fee - b.delivery_fee);
    });
